import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api';
import './CommunityAvatarViewer.css';

function CommunityAvatarViewer({
  creationId,
  onClose,
  onOpenProfile,
  onCreateAvatar,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);

  const [result, setResult] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [failedImageUrl, setFailedImageUrl] = useState(null);

  onCloseRef.current = onClose;

  const id = String(creationId || '');

  // Results are tied to their ID so switching creations never
  // briefly displays the previously opened avatar.
  const current = result?.id === id ? result : null;
  const creation = current?.creation || null;
  const loading = !current;
  const error = current?.error || '';
  const unavailable = current?.unavailable === true;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setResult(null);
    setFailedImageUrl(null);

    async function loadCreation() {
      try {
        if (!/^[a-f0-9]{24}$/i.test(id)) {
          setResult({
            id,
            unavailable: true,
            error: 'This avatar is no longer available.',
          });
          return;
        }

        const { data } = await api.get(
          `/community-avatars/${encodeURIComponent(id)}`,
          { signal: controller.signal }
        );

        if (cancelled) return;

        if (
          String(data?.creation?._id || '') !== id ||
          !data?.creation?.owner?._id
        ) {
          throw new Error('Invalid avatar response.');
        }

        setResult({
          id,
          creation: data.creation,
        });
      } catch (requestError) {
        if (cancelled) return;

        const status = requestError?.response?.status;
        const isUnavailable = status === 404 || status === 400;

        setResult({
          id,
          unavailable: isUnavailable,
          error: isUnavailable
            ? 'This avatar is no longer available.'
            : status === 401
              ? 'Please sign in again to view this avatar.'
              : status === 403
                ? 'This avatar cannot be viewed with your account.'
                : 'Could not load this avatar. Please try again.',
          canRetry: ![400, 401, 403, 404].includes(status),
        });
      }
    }

    loadCreation();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, retryKey]);

  // Match the existing profile viewer's focus and scroll handling.
  useEffect(() => {
    const previousFocus = document.activeElement;
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousOverflow = document.body.style.overflow;

    if (root) root.inert = true;

    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const buttons = Array.from(
        dialogRef.current?.querySelectorAll(
          'button:not([disabled])'
        ) || []
      );

      const first = buttons[0];
      const last = buttons[buttons.length - 1];

      if (!first) return;

      const focusInside = dialogRef.current?.contains(
        document.activeElement
      );

      if (
        event.shiftKey &&
        (!focusInside || document.activeElement === first)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (!focusInside || document.activeElement === last)
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);

      document.body.style.overflow = previousOverflow;

      if (root) root.inert = previousInert;

      if (previousFocus?.isConnected) {
        previousFocus.focus?.({ preventScroll: true });
      }
    };
  }, []);

  function handleOpenOwner() {
    const username = creation?.owner?.username;

    if (!username || typeof onOpenProfile !== 'function') return;

    onCloseRef.current?.();
    onOpenProfile(username);
  }

  function handleCreateAvatar() {
    onCloseRef.current?.();
    onCreateAvatar?.();
  }

  const imageUnavailable =
    !creation?.imageUrl ||
    failedImageUrl === creation.imageUrl;

  return createPortal(
    <div
      className="community-avatar-viewer"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCloseRef.current?.();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="community-avatar-viewer__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={
          creation?.name
            ? `${creation.name} avatar`
            : 'Community avatar'
        }
      >
        <header className="community-avatar-viewer__header">
          <span
            className="community-avatar-viewer__name"
            title={creation?.name || ''}
          >
            {creation?.name ||
              (loading ? 'Loading avatar…' : 'Community avatar')}
          </span>

          <button
            ref={closeRef}
            type="button"
            className="community-avatar-viewer__close"
            onClick={() => onCloseRef.current?.()}
            aria-label="Close avatar"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m6 6 12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {loading ? (
          <div
            className="community-avatar-viewer__status"
            role="status"
          >
            Loading avatar…
          </div>
        ) : error ? (
          <div
            className="community-avatar-viewer__status"
            role="status"
          >
            <p>{error}</p>

            {!unavailable && current?.canRetry && (
              <button
                type="button"
                className="community-avatar-viewer__retry"
                onClick={() => {
                  setResult(null);
                  setRetryKey((value) => value + 1);
                }}
              >
                Retry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="community-avatar-viewer__image-wrap">
              {imageUnavailable ? (
                <p role="status">Avatar image unavailable</p>
              ) : (
                <img
                  key={creation.imageUrl}
                  src={creation.imageUrl}
                  alt={creation.name || 'Community avatar'}
                  className="community-avatar-viewer__image"
                  draggable={false}
                  onError={() =>
                    setFailedImageUrl(creation.imageUrl)
                  }
                />
              )}
            </div>

            <div className="community-avatar-viewer__owner">
              <span className="community-avatar-viewer__owner-label">
                Created by
              </span>

              {creation.owner.username &&
              typeof onOpenProfile === 'function' ? (
                <button
                  type="button"
                  className="community-avatar-viewer__owner-button"
                  onClick={handleOpenOwner}
                >
                  @{creation.owner.username}
                </button>
              ) : (
                <span className="community-avatar-viewer__owner-name">
                  {creation.owner.name ||
                    creation.owner.username ||
                    'Community creator'}
                </span>
              )}
            </div>
          </>
        )}

        {typeof onCreateAvatar === 'function' && (
          <footer className="community-avatar-viewer__footer">
            <button
              type="button"
              className="community-avatar-viewer__create"
              onClick={handleCreateAvatar}
            >
              Create my avatar
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}

export default CommunityAvatarViewer;
